import { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/apiClient';

export interface PendingItem {
  id: number;
  source_table?: string;
  source_id?: number;
  subject_id?: number;
  subject_first_name?: string;
  subject_last_name?: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  action_url?: string;
}

export interface PendingGroup {
  task_type: string;
  title: string;
  module: string;
  assignee_role: string;
  due_date: string | null;
  count: number;
  items: PendingItem[];
}

interface PendingItemsResponse {
  data: PendingGroup[];
}

interface UsePendingItemsDataProps {
  assigneeRole?: string;
  taskType?: string;
  enabled?: boolean;
}

export const usePendingItemsData = ({
  assigneeRole,
  taskType,
  enabled = true,
}: UsePendingItemsDataProps = {}) => {
  const [data, setData] = useState<PendingGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize filter values to avoid unnecessary re-fetches
  const filterKey = useMemo(
    () => `${assigneeRole || ''}::${taskType || ''}`,
    [assigneeRole, taskType],
  );

  useEffect(() => {
    if (!enabled) {
      setData([]);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string> = {};
        if (assigneeRole) params.assigneeRole = assigneeRole;
        if (taskType) params.taskType = taskType;

        const response = await apiClient.get<PendingItemsResponse>('/events/pending-items', { params });

        const responseData = response.data;

        if (Array.isArray(responseData?.data)) {
          setData(responseData.data);
        } else if (Array.isArray(responseData)) {
          setData(responseData as unknown as PendingGroup[]);
        } else {
          console.warn('[usePendingItemsData] Unexpected response format:', responseData);
          setData([]);
        }
      } catch (err: any) {
        console.error('[usePendingItemsData] Error fetching data:', err);

        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this data.');
        } else if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Please try again.');
        } else if (err.message?.includes('Network Error')) {
          setError('Unable to connect to the server.');
        } else {
          setError(err.response?.data?.message || 'Failed to load pending items');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filterKey, enabled]);

  return { data, loading, error };
};
